import { defineStore } from 'pinia';
import  { ApiService }  from '~/services/ApiService';
import type {SignedInUser} from "~/types/SignedInUser";
import {ApiError} from "~/utils/ApiError";
import type {Envelope} from "~/types/Envelope";
import type {ResetPasswordCode} from "~/types/ResetPasswordCode";

export const useUserStore = defineStore('user', () => {

    const api = ApiService.getInstance();

    const user = ref<SignedInUser | null>(null);
    const isAuthenticated = computed(() => !!user.value && user.value.accessToken?.length > 0);
    const darkMode = ref<Boolean>(true);
    const menuExpanded = ref<Boolean>(false);
    const reportDownloaded = ref<Boolean>(false);

    const signUp = async (fullName: string, email: string, whatsappNumber: string, password: string, acceptedTerms: boolean) : Promise<void> => {

        try {
            const envelope: Envelope<SignedInUser | null> = await api.signUp(fullName, email, whatsappNumber, password, acceptedTerms);

            if (envelope.success) {
                return
            } else {
                throw new ApiError(envelope);
            }
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            } else if (error instanceof Error) {
                throw Error(error.message);
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const signIn = async (email: string, password: string) : Promise<SignedInUser> => {

        try {
            const envelope: Envelope<SignedInUser | null> = await api.signIn(email, password);

            if (envelope.success && envelope.data) {
                const data = envelope.data
                api.setToken(data.accessToken);
                user.value = data;
                return data as SignedInUser;
            } else {
                throw new ApiError(envelope);
            }
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            } else if (error instanceof Error) {
                throw Error(error.message);
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const signOut = () => {
        api.clearToken();
        user.value = null;
    };

    const resetPasswordCaptureWhatsapp = async (whatsappNumber:string) : Promise<any> => {
        try {
            const envelope: Envelope<string | null> = await api.resetPasswordCaptureWhatsapp(whatsappNumber)
            if (envelope.success && envelope.data) {
            } else {
                throw new ApiError(envelope);
            }
            return
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            } else if (error instanceof Error) {
                throw Error(error.message);
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const resetPasswordCaptureCode = async (whatsappNumber:string, verificationCode:string) : Promise<ResetPasswordCode> => {
        try {
            const envelope: Envelope<ResetPasswordCode> = await api.resetPasswordCaptureCode(whatsappNumber, verificationCode)
            if (envelope.success && envelope.data) {
                return envelope.data
            } else {
                throw new ApiError(envelope);
            }
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            } else if (error instanceof Error) {
                throw Error(error.message);
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const resetPasswordNewPassword = async (whatsappNumber:string, resetPasswordCode:string, newPassword:string) : Promise<any> => {
        try {
            const envelope: Envelope<ResetPasswordCode> = await api.resetPasswordNewPassword(whatsappNumber, resetPasswordCode, newPassword)
            if (envelope.success && envelope.data) {
            } else {
                throw new ApiError(envelope);
            }
            return
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            } else if (error instanceof Error) {
                throw Error(error.message);
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    return {
        darkMode,
        isAuthenticated,
        menuExpanded,
        reportDownloaded,
        resetPasswordCaptureWhatsapp,
        resetPasswordCaptureCode,
        resetPasswordNewPassword,
        signIn,
        signOut,
        signUp,
        user,
    };
});
